/**
 * `DICCIONARIO_CAMPOS` — the 17 template fields, as the spine of the portal.
 *
 * The column names are the client's own Excel headers and stay `snake_case`
 * everywhere: one name links the import template, the API, the PDF and a support
 * conversation (design spec §3). Everything the system generates is `camelCase`.
 *
 * Two properties here are legal facts rather than presentation choices:
 *
 *  - `onPdf: false` for `nif_conductor`. It is mandatory to capture and **never
 *    printed** (`DICCIONARIO_CAMPOS` #17); the form has to say so out loud.
 *  - `party` is the party liable under the BOE, which is what the €401-per-party
 *    penalty attaches to and what the validation rail groups by.
 */

import type { TFunction } from 'i18next'
import type { LiableParty } from '@deca/api'

/** The four numbered sections, matching the PDF's own 1–4 (design spec §7). */
export type FieldSection = 'cargador' | 'transportista' | 'envio' | 'transporte'

/** Where a value lives: on the document, or per consignment (BOE Sixth). */
export type FieldScope = 'document' | 'consignment'

export interface FieldDef {
  /** The Excel column name — the identity of the field across every surface. */
  column: string
  /**
   * The Spanish label exactly as `DICCIONARIO_CAMPOS` fixes it and the PDF
   * prints it. Not a translation — a legal fact, so it lives here rather than in
   * the i18n catalogues, and it is what the server's `FieldError.labelEs`
   * matches. In an English UI it is shown beside the English label, because it
   * is the wording that will appear on the document the user is producing.
   */
  labelEs: string
  /** Dictionary number, 1–17. */
  no: number
  section: FieldSection
  scope: FieldScope
  mandatory: boolean
  /** Printed on the DeCA. Only `nif_conductor` is false. */
  onPdf: boolean
  party: LiableParty
  /** Form control to render. */
  /**
   * `date` is a calendar day; `fecha_realizacion`'s optional time of day is a second control the
   * form draws beside it, because HTML's `datetime-local` cannot express an absent time.
   */
  control: 'text' | 'textarea' | 'date' | 'number' | 'select'
  maxLength?: number
  /** Amendable in place under BOE Fifth.1 — the parties are not. */
  modifiableInPlace: boolean
}

export const FIELDS: readonly FieldDef[] = [
  {
    column: 'cargador_razon_social',
    labelEs: 'Razón social del cargador contractual',
    no: 1,
    section: 'cargador',
    scope: 'document',
    mandatory: true,
    onPdf: true,
    party: 'CARGADOR',
    control: 'text',
    maxLength: 200,
    modifiableInPlace: false,
  },
  {
    column: 'cargador_nif',
    labelEs: 'NIF del cargador contractual',
    no: 2,
    section: 'cargador',
    scope: 'document',
    mandatory: true,
    onPdf: true,
    party: 'CARGADOR',
    control: 'text',
    maxLength: 20,
    modifiableInPlace: false,
  },
  {
    column: 'cargador_domicilio',
    labelEs: 'Domicilio del cargador contractual',
    no: 3,
    section: 'cargador',
    scope: 'document',
    mandatory: true,
    onPdf: true,
    party: 'CARGADOR',
    control: 'textarea',
    maxLength: 300,
    modifiableInPlace: false,
  },
  {
    column: 'transportista_razon_social',
    labelEs: 'Razón social del transportista efectivo',
    no: 4,
    section: 'transportista',
    scope: 'document',
    mandatory: true,
    onPdf: true,
    party: 'CARGADOR',
    control: 'text',
    maxLength: 200,
    modifiableInPlace: false,
  },
  {
    column: 'transportista_nif',
    labelEs: 'NIF del transportista efectivo',
    no: 5,
    section: 'transportista',
    scope: 'document',
    mandatory: true,
    onPdf: true,
    party: 'CARGADOR',
    control: 'text',
    maxLength: 20,
    modifiableInPlace: false,
  },
  {
    column: 'transportista_domicilio',
    labelEs: 'Domicilio del transportista efectivo',
    no: 6,
    section: 'transportista',
    scope: 'document',
    mandatory: true,
    onPdf: true,
    party: 'CARGADOR',
    control: 'textarea',
    maxLength: 300,
    modifiableInPlace: false,
  },
  {
    column: 'origen_domicilio',
    labelEs: 'Lugar de origen',
    no: 7,
    section: 'envio',
    scope: 'consignment',
    mandatory: true,
    onPdf: true,
    party: 'CARGADOR',
    control: 'textarea',
    maxLength: 300,
    modifiableInPlace: true,
  },
  {
    column: 'destino_domicilio',
    labelEs: 'Lugar de destino',
    no: 8,
    section: 'envio',
    scope: 'consignment',
    mandatory: true,
    onPdf: true,
    party: 'CARGADOR',
    control: 'textarea',
    maxLength: 300,
    modifiableInPlace: true,
  },
  {
    column: 'fecha_realizacion',
    labelEs: 'Fecha de realización',
    no: 9,
    section: 'envio',
    scope: 'document',
    mandatory: true,
    onPdf: true,
    party: 'TRANSPORTISTA',
    control: 'date',
    modifiableInPlace: true,
  },
  {
    column: 'naturaleza_mercancia',
    labelEs: 'Naturaleza de la mercancía',
    no: 10,
    section: 'envio',
    scope: 'consignment',
    mandatory: true,
    onPdf: true,
    party: 'CARGADOR',
    control: 'textarea',
    maxLength: 500,
    modifiableInPlace: true,
  },
  {
    column: 'peso_magnitud',
    labelEs: 'Peso o magnitud',
    no: 11,
    section: 'envio',
    scope: 'consignment',
    mandatory: true,
    onPdf: true,
    party: 'CARGADOR',
    control: 'number',
    modifiableInPlace: true,
  },
  {
    column: 'unidad_medida',
    labelEs: 'Unidad de medida',
    no: 12,
    section: 'envio',
    scope: 'consignment',
    mandatory: true,
    onPdf: true,
    party: 'CARGADOR',
    control: 'select',
    modifiableInPlace: true,
  },
  {
    column: 'matricula_tractor',
    labelEs: 'Matrícula del vehículo tractor',
    no: 13,
    section: 'transporte',
    scope: 'document',
    mandatory: true,
    onPdf: true,
    party: 'TRANSPORTISTA',
    control: 'text',
    maxLength: 20,
    modifiableInPlace: true,
  },
  {
    column: 'matricula_remolque',
    labelEs: 'Matrícula remolque / semirremolque',
    no: 14,
    section: 'transporte',
    scope: 'document',
    // ⚠️ `DICCIONARIO_CAMPOS` marks field 14 `○` — optional — and this is `true` anyway, at the
    // client's explicit request (#63, decision 2026-08-16): the one field where this table is
    // stricter than the dictionary it encodes, and it must stay in step with
    // `backend/src/deca/validation.ts`. ↻ Since 2026-10-01 a rigid truck is the exception:
    // `GoodsCreateForm`'s «Rígido» lifts it for one document, as the server does (spec
    // 2026-10-01-deca-rigido-design.md). The table stays `true`: it describes the field, not the box.
    mandatory: true,
    onPdf: true,
    party: 'TRANSPORTISTA',
    control: 'text',
    maxLength: 20,
    modifiableInPlace: true,
  },
  {
    column: 'autorizacion_especial_circulacion',
    labelEs: 'Autorización especial de circulación',
    no: 15,
    section: 'transporte',
    scope: 'document',
    mandatory: false,
    onPdf: true,
    party: 'TRANSPORTISTA',
    control: 'textarea',
    maxLength: 500,
    modifiableInPlace: true,
  },
  {
    column: 'observaciones',
    labelEs: 'Observaciones',
    no: 16,
    section: 'transporte',
    scope: 'document',
    mandatory: false,
    onPdf: true,
    party: 'CUALQUIERA',
    control: 'textarea',
    maxLength: 1000,
    modifiableInPlace: true,
  },
  {
    column: 'nif_conductor',
    labelEs: 'NIF del conductor',
    no: 17,
    section: 'transporte',
    scope: 'document',
    mandatory: true,
    // Mandatory to capture, never printed — DICCIONARIO_CAMPOS #17. The form
    // carries this as visible helper text, not as a comment in the source.
    onPdf: false,
    party: 'TRANSPORTISTA',
    control: 'text',
    maxLength: 20,
    modifiableInPlace: true,
  },
] as const

const BY_COLUMN = new Map(FIELDS.map((f) => [f.column, f]))

export function fieldDef(column: string): FieldDef | undefined {
  return BY_COLUMN.get(column)
}

export function fieldsForSection(section: FieldSection): FieldDef[] {
  return FIELDS.filter((f) => f.section === section)
}

/** The four sections in the order the PDF prints them. */
export const SECTIONS: readonly FieldSection[] = [
  'cargador',
  'transportista',
  'envio',
  'transporte',
] as const

/**
 * Where a column sits **on the form**: section, then consignment, then the
 * dictionary number.
 *
 * Both lists of "what is still missing" sort by this — the live rail while the
 * user types, and the server's 422 after a refused generation — so the two
 * cannot disagree about the order. Neither is dictionary order: `fecha_realizacion`
 * is field 9 and is drawn in section 4, after the consignment's fields 10–12.
 *
 * An unknown column sorts last on every component. Callers use a stable sort, so
 * such a field keeps its arrival position at the end rather than vanishing —
 * dropping it would hide a mandatory-field failure the server did report.
 */
export function formPosition(column: string, seq?: number | null): [number, number, number] {
  const def = BY_COLUMN.get(column)
  return [
    def ? SECTIONS.indexOf(def.section) : SECTIONS.length,
    seq ?? 0,
    def?.no ?? Number.MAX_SAFE_INTEGER,
  ]
}

/** Sorts in place by `formPosition`, stably. */
export function byFormPosition<T>(
  items: readonly T[],
  columnOf: (item: T) => string,
  seqOf: (item: T) => number | null | undefined,
): T[] {
  return [...items].sort((a, b) => {
    const left = formPosition(columnOf(a), seqOf(a))
    const right = formPosition(columnOf(b), seqOf(b))
    for (let i = 0; i < left.length; i += 1) {
      if (left[i]! !== right[i]!) return left[i]! - right[i]!
    }
    return 0
  })
}

/** Column order for the Excel template and the import review table. */
export const TEMPLATE_COLUMNS: readonly string[] = FIELDS.map((f) => f.column)

/**
 * A field's name, qualified enough to stand outside its section.
 *
 * The party labels are deliberately short — "Razón social", "NIF" — because on
 * the form and on the document the numbered section says whose they are. Two
 * places have no section to lean on: the amendment selector and the validation
 * rail, which groups by *liable party* rather than by section and so lists the
 * shipper's and the carrier's fields together. Unqualified, they are the same
 * three words twice.
 */
export function qualifiedFieldLabel(def: FieldDef, t: TFunction, seq?: number): string {
  const label = t(`fields.${def.column}.label`)
  const qualified =
    def.section === 'cargador' || def.section === 'transportista'
      ? `${t(`sections.${def.section}`)} · ${label}`
      : label
  return seq == null ? qualified : `${qualified} · ${t('create.consignment', { seq })}`
}
